import { Fragment } from 'react';
import { api } from '../lib/api';
import type { ProviderInfo } from '../lib/api';
import { useState } from 'react';
import { ErrorNotice, Loading, Notice, Panel, useAsync } from '../components/ui';

interface Health {
  status: string;
  version: string;
  environment: string;
  deviceProvider: string;
  checks: Record<string, string>;
  timestamp: string;
}

export default function SettingsPage() {
  const providers = useAsync(
    (signal) => api.get<{ providers: ProviderInfo[]; active: string }>('/providers', signal),
    [],
  );
  const health = useAsync((signal) => api.get<Health>('/health', signal), [], 30_000);
  const me = useAsync((signal) => api.get<{ user: { fullName: string; email: string; role: string } }>('/auth/me', signal), []);

  const [fullName, setFullName] = useState('');

  const alertSetting = useAsync(
    (signal) => api.get<{ deviceAlertsEnabled: boolean }>('/settings/alerts', signal),
    [],
  );

  // Held locally so the switch responds immediately, then reconciled with what
  // the server confirms. An administrator flipping a monitoring switch needs to
  // see it move; they should not have to guess whether the click registered.
  const [alertsEnabled, setAlertsEnabled] = useState<boolean | null>(null);
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertError, setAlertError] = useState<unknown>(null);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMessage, setNameMessage] = useState<string | null>(null);
  const [nameError, setNameError] = useState<unknown>(null);

  const displayName = fullName || me.data?.user.fullName || '';

  const alertsOn = alertsEnabled ?? alertSetting.data?.deviceAlertsEnabled ?? true;

  const toggleAlerts = async (next: boolean) => {
    setAlertBusy(true);
    setAlertError(null);
    try {
      const result = await api.put<{ deviceAlertsEnabled: boolean }>('/settings/alerts', {
        enabled: next,
      });
      setAlertsEnabled(result.deviceAlertsEnabled);
    } catch (err) {
      setAlertError(err);
    } finally {
      setAlertBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Settings</h2>
          <p>System status and the device management provider in use.</p>
        </div>
      </div>

      <ErrorNotice error={providers.error} />

      <Panel title="Your profile">
        <p className="small muted" style={{ marginTop: 0 }}>
          This is the name shown in the top bar. It is stored on your administrator
          account — it is not a theme label, so it stays until you change it here.
        </p>
        <label>
          <span>Display name</span>
          <input
            value={displayName}
            onChange={(e) => setFullName(e.target.value)}
            maxLength={80}
            placeholder="Your name"
          />
        </label>
        <p className="small muted">Signed in as {me.data?.user.email} ({me.data?.user.role})</p>
        {nameMessage && <Notice kind="success">{nameMessage}</Notice>}
        <ErrorNotice error={nameError} />
        <div className="actions">
          <button
            className="primary"
            type="button"
            disabled={nameBusy || displayName.trim().length < 2}
            onClick={async () => {
              setNameBusy(true);
              setNameError(null);
              setNameMessage(null);
              try {
                await api.patch('/auth/me', { fullName: displayName.trim() });
                setNameMessage('Display name updated.');
                window.dispatchEvent(new Event('tab-admin-updated'));
                me.reload();
              } catch (err) {
                setNameError(err);
              } finally {
                setNameBusy(false);
              }
            }}
          >
            {nameBusy ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </Panel>

      <Panel title="System health">
        {health.loading && !health.data ? (
          <Loading what="health" />
        ) : (
          <dl className="kv">
            <dt>Status</dt>
            <dd>
              <span className={`badge ${health.data?.status === 'ok' ? 'ok' : 'danger'}`}>
                {health.data?.status.toUpperCase()}
              </span>
            </dd>
            <dt>API version</dt><dd>{health.data?.version}</dd>
            <dt>Environment</dt><dd>{health.data?.environment}</dd>
            {Object.entries(health.data?.checks ?? {}).map(([name, state]) => (
              <Fragment key={name}>
                <dt>{name}</dt>
                <dd>
                  <span className={`badge ${state === 'up' ? 'ok' : 'danger'}`}>{state.toUpperCase()}</span>
                </dd>
              </Fragment>
            ))}
          </dl>
        )}
      </Panel>

      <Panel title="Device management providers">
        <p className="small muted" style={{ marginTop: 0 }}>
          The dashboard and all business logic talk to a provider abstraction, never to a specific
          provider. Switching provider is a backend configuration change; nothing in this UI
          depends on which one is active.
        </p>

        {providers.loading && !providers.data ? (
          <Loading what="providers" />
        ) : (
          (providers.data?.providers ?? []).map((p) => (
            <div
              key={p.id}
              className="panel"
              style={{ background: p.active ? 'var(--info-bg)' : 'var(--bg)', marginBottom: 12 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <strong>{p.displayName}</strong>
                <div className="actions">
                  {p.active && <span className="badge info">ACTIVE</span>}
                  {p.available
                    ? <span className="badge ok">AVAILABLE</span>
                    : <span className="badge danger">UNAVAILABLE</span>}
                </div>
              </div>

              {!p.available && (
                <Notice kind="warning">
                  <div>{p.reason}</div>
                  {p.remediation && <p style={{ margin: '8px 0 0' }}><strong>To enable:</strong> {p.remediation}</p>}
                </Notice>
              )}

              <dl className="kv" style={{ marginTop: 8 }}>
                <dt>Enrollment</dt><dd className="small">{p.capabilities.enrollmentMethods.join(', ')}</dd>
                <dt>Delivery</dt>
                <dd className="small">
                  {p.capabilities.delivery}
                  {p.capabilities.delivery === 'poll' && (
                    <span className="muted"> — commands reach a device only when it checks in</span>
                  )}
                </dd>
                <dt>Arbitrary APKs</dt>
                <dd className="small">{p.capabilities.supportsArbitraryApkInstall ? 'yes' : 'no'}</dd>
                <dt>Survives factory reset</dt>
                <dd className="small">
                  {p.capabilities.survivesFactoryReset
                    ? <span className="badge ok">YES</span>
                    : <span className="badge danger">NO</span>}
                  <div className="muted" style={{ marginTop: 4 }}>{p.capabilities.factoryResetNote}</div>
                </dd>
                <dt>Commands</dt>
                <dd className="small mono">{p.capabilities.supportedCommands.join(', ') || 'none'}</dd>
              </dl>
            </div>
          ))
        )}
      </Panel>

      <Panel title="Device state alerts">
        <p className="small muted" style={{ marginTop: 0 }}>
          The heartbeat monitor raises an alert when a device stops checking in, comes back,
          or re-enrols with a new identity after going silent. These are observations, not
          administrative actions, so they are kept out of the audit log and can be switched
          off here.
        </p>

        {alertSetting.loading && alertsEnabled === null ? (
          <Loading />
        ) : (
          <label
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={alertsOn}
              disabled={alertBusy}
              onChange={(e) => void toggleAlerts(e.target.checked)}
            />
            <span>
              <strong>Raise alerts for device state changes</strong>
              <div className="small muted">
                {alertsOn
                  ? 'Offline, delayed check-in, reconnection and possible-reset alerts are being recorded.'
                  : 'No new alerts will be recorded. Device state is still tracked and still shown on each device page — only the alert feed is silenced.'}
              </div>
            </span>
          </label>
        )}

        {alertError ? <ErrorNotice error={alertError} /> : null}

        <Notice kind="info">
          Turning alerts off does not affect the audit log. Administrative actions are always
          recorded, including this switch.
        </Notice>
      </Panel>

      <Panel title="Security notes">
        <ul className="small" style={{ marginTop: 0, paddingLeft: 20 }}>
          <li>No Google service-account key, storage credential or device secret ever reaches this browser.</li>
          <li>Enrollment tokens are stored as SHA-256 hashes and shown in full exactly once.</li>
          <li>Each device authenticates with its own secret; one device cannot act for another.</li>
          <li>The APK bucket is private. Devices receive short-lived, single-object presigned URLs.</li>
          <li>Audit metadata is scrubbed of passwords, tokens, keys and Wi-Fi credentials before being written.</li>
        </ul>
      </Panel>
    </>
  );
}
